import { useEffect, useMemo } from "react";
import { TextureLoader } from "three";
import * as THREE from "three";
import { finishesData } from "../../utils/finsishesData";

/**
 * Custom hook for applying and updating textures to a material based on provided finish data.
 *
 * This hook:
 * - Finds the appropriate finish data from a predefined list.
 * - Loads textures asynchronously using the `TextureLoader`.
 * - Applies loaded textures and updates material properties such as color, roughness, and metalness.
 * - Resets material properties when no texture URL is provided.
 * - Cleans up by disposing of textures when the component unmounts or dependencies change.
 *
 * @param {THREE.Material} material - The material to which textures and properties will be applied.
 * @param {string} finishes - ID of the finish data to be applied.
 * @returns {THREE.TextureLoader} - The texture loader instance used for loading textures.
 */

export default function useTextureCustom(material, finishes) {
  // Find the finish data that matches the provided finishes ID
  const applyFinish = finishesData.find((item) => item.id === finishes);

  const {
    diffuseUrl,
    normalUrl,
    ormUrl,
    aoUrl,
    aoRepeat,
    roughnessUrl,
    metalnessUrl,
    repeat,
    normalRepeat,
    ormRepeat,
    roughnessRepeat,
    metalnessRepeat,
    color,
    useAo,
    useRoughness,
    useMetalness,
    roughness,
    normalScale,
    metalness,
    clearcoat,
    clearcoatRoughness,
    ior,
  } = applyFinish || {};

  // Memoize the TextureLoader to avoid recreating it on every render
  const loader = useMemo(() => new TextureLoader(), []);

  useEffect(() => {
    // Exit early if material is not provided
    if (!material) return;

    // Array to keep track of loaded textures
    const textures = [];

    // Function to load a texture asynchronously
    const loadTextureAsync = async (url, settings, isSrgb) => {
      return new Promise((resolve) => {
        loader.load(url, (texture) => {
          // Set texture properties
          texture.flipY = false;
          if (isSrgb) {
            texture.colorSpace = THREE.SRGBColorSpace;
          }
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(settings[0], settings[1]);

          // Add texture to the array and resolve the promise
          textures.push(texture);
          resolve(texture);
        });
      });
    };

    // Function to update the material with textures and properties
    const updateMaterialTextures = async () => {
      // Array to hold promises for loading textures
      const texturePromises = [];

      // Array of material names that require specific reset handling
      const materialCheck = [
        "handle",
        "wheel_handle",
        "lock_securam_toplit",
        "securam_topit_lock",
        "securam_toplit_lock",
        "securam_topit",
        "securam_prologic",
        "securam_prologic_lock",
        "mechanical_lock",
        "prologic_elock_body",
        "wheel_handle_outer",
        "wheel_handle_door_inner",
        "toplit_elock_body",
        "handle_body",
      ];

      // Reset material properties if the material name does not match the check list
      if (!materialCheck.includes(material.name)) {
        material.map = null;
        material.normalMap = null;
      }
      material.roughnessMap = null;
      material.metalnessMap = null;
      material.aoMap = null;
      material.normalScale.set(1, 1);
      material.roughness = 1; // Default roughness value
      material.metalness = 0; // Default metalness value
      material.clearcoat = 0; // Default clearcoat value
      material.clearcoatRoughness = 0; // Default clearcoat roughness value
      material.ior = 1.5; // Default index of refraction value

      // Load and apply diffuse texture if URL is provided
      if (diffuseUrl) {
        texturePromises.push(
          loadTextureAsync(diffuseUrl, repeat ? repeat : [1, 1], true).then(
            (texture) => {
              material.map = texture;
            }
          )
        );
      }

      // Load and apply normal texture if URL is provided
      if (normalUrl) {
        texturePromises.push(
          loadTextureAsync(
            normalUrl,
            normalRepeat ? normalRepeat : [1, 1],
            false
          ).then((texture) => {
            material.normalMap = texture;
            if (normalScale !== undefined) {
              material.normalScale.set(normalScale, normalScale);
            }
          })
        );
      }

      // Load and apply AO texture if URL is provided
      if (aoUrl) {
        texturePromises.push(
          loadTextureAsync(aoUrl, aoRepeat ? aoRepeat : [1, 1], false).then(
            (texture) => {
              material.aoMap = texture;
            }
          )
        );
      }

      // Load and apply roughness texture if URL is provided
      if (roughnessUrl) {
        texturePromises.push(
          loadTextureAsync(
            roughnessUrl,
            roughnessRepeat ? roughnessRepeat : [1, 1],
            false
          ).then((texture) => {
            material.roughnessMap = texture;
          })
        );
      }

      // Load and apply metalness texture if URL is provided
      if (metalnessUrl) {
        texturePromises.push(
          loadTextureAsync(
            metalnessUrl,
            metalnessRepeat ? metalnessRepeat : [1, 1],
            false
          ).then((texture) => {
            material.metalnessMap = texture;
          })
        );
      }

      // Load and apply ORM texture if URL is provided and any of its associated flags are set
      if (ormUrl && (useAo || useRoughness || useMetalness)) {
        texturePromises.push(
          loadTextureAsync(ormUrl, ormRepeat ? ormRepeat : [1, 1], false).then(
            (texture) => {
              if (useAo) material.aoMap = texture;
              if (useRoughness) material.roughnessMap = texture;
              if (useMetalness) material.metalnessMap = texture;
            }
          )
        );
      }

      // Set material color if color is provided
      if (color) material.color.set(color);

      // Set material properties if values are provided
      if (roughness !== undefined) material.roughness = roughness;
      if (metalness !== undefined) material.metalness = metalness;
      if (clearcoat !== undefined) material.clearcoat = clearcoat;
      if (clearcoatRoughness !== undefined)
        material.clearcoatRoughness = clearcoatRoughness;
      if (ior !== undefined) material.ior = ior;

      // Wait for all textures to be loaded and applied
      await Promise.all(texturePromises);

      // Mark material as needing update to reflect changes
      material.needsUpdate = true;

      // Optionally log the material state for debugging
      // console.log(
      //   "MaterialSet on ",
      //   material.name,
      //   "\nDiffuse map: ",
      //   material.map,
      //   "\nNormal map: ",
      //   material.normalMap,
      //   "\nNormal scale: ",
      //   material.normalScale,
      //   "\nRoughness map: ",
      //   material.roughnessMap,
      //   "\nRoughness: ",
      //   material.roughness,
      //   "\nMetalness map: ",
      //   material.metalnessMap,
      //   "\nMetalness: ",
      //   material.metalness,
      //   "\nAO map: ",
      //   material.aoMap,
      //   "\nColor: ",
      //   material.color
      // );
      // console.log(material.name, material);
    };

    // Call the function to update material textures
    updateMaterialTextures();

    // Cleanup function to dispose of textures when component unmounts or dependencies change
    return () => {
      textures.forEach((texture) => {
        texture.dispose(); // Dispose of each texture
      });
    };
  }, [
    material,
    diffuseUrl,
    normalUrl,
    ormUrl,
    aoUrl,
    roughnessUrl,
    metalnessUrl,
    repeat,
    normalRepeat,
    ormRepeat,
    aoRepeat,
    roughnessRepeat,
    metalnessRepeat,
    color,
    useAo,
    useRoughness,
    useMetalness,
    roughness,
    normalScale,
    metalness,
    clearcoat,
    clearcoatRoughness,
    ior,
  ]);

  return loader;
}
